import './BritishInsignia.css';

/**
 * 2026-06-04 British-flavor pass (user feedback) — real Public-Domain /
 * CC0 insignia for the ScenarioRunner header.
 *
 * Per user direction "dont invent thes images" + "get them from the web"
 * + "get awesome images", these load REAL assets bundled in the
 * vibe-designer SPA's /public/insignia/ folder rather than hand-drawn
 * SVG approximations:
 *
 *   - union-jack.svg       — PD-UKGov, sourced from Wikimedia Commons
 *                            (Flag_of_the_United_Kingdom_(1-2).svg, the
 *                            naval/admiral 1:2 ratio Union Jack).
 *   - st-edwards-crown.svg — CC0, sourced from Wikimedia Commons
 *                            (St_Edward's_Crown_(plain)_3D.svg), the
 *                            canonical British monarchy crown used on
 *                            official HMG / MI6 insignia.
 *
 * Licenses + attribution + bundle-impact note:
 *   `vibe-designer/frontend/public/insignia/LICENSES.md`.
 *
 * Asset placement rationale: `/public/insignia/` lives under
 * vibe-designer (the SPA host), NOT briefing-room-module — same
 * lesson as the audio assets (the briefing-room-module's public/ is
 * NOT copied into the dist build; vite's publicDir is vibe-designer's
 * own public/).
 */

export interface UnionJackBadgeProps {
  className?: string;
  testid?: string;
  /** Pixel height; width derives from the 1:2 aspect ratio (so width = 2h). */
  height?: number;
}

export function UnionJackBadge({
  className = 'br-union-jack',
  testid = 'br-union-jack',
  height = 18,
}: UnionJackBadgeProps) {
  return (
    <img
      className={className}
      data-testid={testid}
      src="/insignia/union-jack.svg"
      alt="Union Jack"
      height={height}
      width={height * 2}
    />
  );
}

export interface CrownSilhouetteProps {
  className?: string;
  testid?: string;
  /** Pixel height; width derives from the ~1.07:1 aspect of the source. */
  height?: number;
}

export function CrownSilhouette({
  className = 'br-crown',
  testid = 'br-crown',
  height = 24,
}: CrownSilhouetteProps) {
  return (
    <img
      className={className}
      data-testid={testid}
      src="/insignia/st-edwards-crown.svg"
      alt="St Edward's Crown"
      height={height}
      width={Math.round(height * 1.07)}
    />
  );
}

/**
 * Combined insignia row used in the ScenarioRunner header. Order:
 * Union Jack · Crown. Both stay small so the header reads as
 * "British government dossier" without becoming busy.
 */
export interface BritishInsigniaProps {
  className?: string;
  testid?: string;
  height?: number;
}

export function BritishInsignia({
  className = 'br-british-insignia',
  testid = 'br-british-insignia',
  height = 18,
}: BritishInsigniaProps) {
  return (
    <span className={className} data-testid={testid}>
      <UnionJackBadge height={height} />
      <CrownSilhouette height={height + 6} />
    </span>
  );
}
